import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import talentLogo2 from "../assets/talentlogo2.png";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import { clearManageContext, loadManageContext, loadManageSelectedLocations, saveManageContext } from "./manageContext";
import { clearManageSession, manageFetch } from "./manageSession";
import { checkInTimeStatusFromManageRow } from "./manageCheckInTimeStatus";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import { formatAppDate, formatAppIntlLabel, formatAppTime, getAppTodayIso } from "../utils/appDateTime";
import "../components/ShiftDetailModal.css";

const EMPTY_COUNTS = {
  future: 0,
  late: 0,
  checkedIn: 0,
  pendingSignoff: 0,
  pendingSignoffReview: 0,
  signoffComplete: 0,
  noShow: 0,
};

function toDateOnly(value) {
  if (!value) return null;
  const s = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return s;
}

function getTodayDateOnly() {
  return getAppTodayIso();
}

function shiftDateOnly(dateOnly, offsetDays) {
  if (!dateOnly) return getTodayDateOnly();
  const parts = String(dateOnly).split("-").map((p) => Number(p));
  if (parts.length !== 3 || parts.some((v) => !Number.isFinite(v))) {
    return getTodayDateOnly();
  }
  const [year, month, day] = parts;
  const utcMs = Date.UTC(year, month - 1, day + offsetDays);
  return new Date(utcMs).toISOString().slice(0, 10);
}

function scheduleHeadingFromDate(dateOnly) {
  const target = toDateOnly(dateOnly) || getTodayDateOnly();
  const today = getTodayDateOnly();
  const diff =
    Math.round(
      (new Date(`${target}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) /
        86400000,
    ) || 0;
  if (diff === 0) return "Today's Schedule";
  if (diff === 1) return "Tomorrow's Schedule";
  if (diff === -1) return "Yesterday's Schedule";
  return `Schedule for ${formatAppIntlLabel(`${target}T12:00:00Z`, {
    month: "long",
    day: "numeric",
  })}`;
}

function shortDateLabel(dateOnly) {
  const normalized = toDateOnly(dateOnly) || getTodayDateOnly();
  return formatAppIntlLabel(`${normalized}T12:00:00Z`, {
    month: "short",
    day: "numeric",
  });
}

/** Aggregate top summary cards from the same rules as row badges (aligns with API rows + actual_state). */
function summaryCountsFromRowBadges(rows) {
  const counts = { ...EMPTY_COUNTS };
  rows.forEach((row) => {
    const { badgeClass } = checkInTimeStatusFromManageRow(row);
    switch (badgeClass) {
      case "signoff-complete":
        counts.signoffComplete += 1;
        break;
      case "checked-in":
        counts.checkedIn += 1;
        break;
      case "late":
        counts.late += 1;
        break;
      case "future":
        counts.future += 1;
        break;
      case "pending":
        if (row.statusKey === "pendingSignoffReview") {
          counts.pendingSignoffReview += 1;
        } else {
          counts.pendingSignoff += 1;
        }
        break;
      case "pending-review":
        counts.pendingSignoffReview += 1;
        break;
      default:
        break;
    }
  });
  return counts;
}

export default function ManageDashboard() {
  usePageMeta("Manage Dashboard");
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [liveShift, setLiveShift] = useState(null);
  const [groupSummary, setGroupSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedLocationNames, setSelectedLocationNames] = useState([]);
  const [resolvedLocationIds, setResolvedLocationIds] = useState([]);
  const requestSeqRef = useRef(0);
  const [capabilities, setCapabilities] = useState(
    manageContextCapabilitiesFromLocation(location),
  );

  const manageContext = useMemo(() => loadManageContext(location), [location]);
  const {
    shiftId,
    clientName,
    clientId,
    locationName,
    startDate,
    endDate,
    shiftDate,
    clientDeviceId,
    position,
    staffName,
    dateLabel,
    capabilities: contextCapabilities,
  } = manageContext;
  const contextDate = toDateOnly(startDate) || toDateOnly(shiftDate) || getTodayDateOnly();
  const [activeDate, setActiveDate] = useState(contextDate);

  function manageContextCapabilitiesFromLocation(currentLocation) {
    const nextContext = loadManageContext(currentLocation);
    return nextContext?.capabilities || null;
  }

  useEffect(() => {
    saveManageContext(manageContext);
  }, [manageContext]);

  useEffect(() => {
    let cancelled = false;

    const loadSelectedLocationNames = async () => {
      const clientIdForLookup = clientId || manageContext.clientId || null;
      if (!clientIdForLookup) {
        setSelectedLocationNames([]);
        setResolvedLocationIds([]);
        return;
      }

      const selectedIds =
        Array.isArray(manageContext.selectedLocationIds) && manageContext.selectedLocationIds.length
          ? manageContext.selectedLocationIds.map((id) => String(id)).filter(Boolean)
          : loadManageSelectedLocations({
              clientId: clientIdForLookup,
              clientDeviceId,
            });

      if (!selectedIds.length) {
        setSelectedLocationNames([]);
        setResolvedLocationIds([]);
        return;
      }

      try {
        const res = await manageFetch(
          `${BASE_URL}/api/jobs/manage/device-locations?clientId=${encodeURIComponent(clientIdForLookup)}`,
        );
        if (!res.ok) throw new Error(`Failed to load locations (${res.status})`);
        const payload = await res.json().catch(() => []);
        const rows = Array.isArray(payload) ? payload : [];
        const matchedRows = rows.filter((row) => selectedIds.includes(String(row?.id)));
        const names = matchedRows.map((row) => row?.label || row?.name).filter(Boolean);
        if (!cancelled) setSelectedLocationNames(names);
        if (!cancelled) setResolvedLocationIds(matchedRows.map((row) => String(row?.id)).filter(Boolean));
      } catch {
        if (!cancelled) setSelectedLocationNames([]);
        if (!cancelled) setResolvedLocationIds([]);
      }
    };

    loadSelectedLocationNames();
    return () => {
      cancelled = true;
    };
  }, [clientId, clientDeviceId, manageContext.clientId, manageContext.selectedLocationIds]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setActiveDate(contextDate);
  }, [contextDate]);

  useEffect(() => {
    const controller = new AbortController();
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;

    const loadLiveShiftData = async () => {
      if (!shiftId && !manageContext.timesheetId && !clientId) {
        setLiveShift(null);
        return;
      }

      const selectedIds =
        Array.isArray(manageContext.selectedLocationIds) && manageContext.selectedLocationIds.length > 0
          ? manageContext.selectedLocationIds.map((id) => String(id)).filter(Boolean)
          : loadManageSelectedLocations({ clientId: clientId || manageContext.clientId || null, clientDeviceId });
      if (selectedIds.length > 0 && selectedLocationNames.length === 0) {
        return;
      }

      setLoading(true);
      setLoadError("");

        if (requestSeq !== requestSeqRef.current) return;
      try {
        const params = new URLSearchParams();
        if (shiftId) params.set("shiftId", String(shiftId));
        if (manageContext.timesheetId) {
          params.set("timesheetId", String(manageContext.timesheetId));
        }
        if (clientId) params.set("clientId", String(clientId));
        if (clientDeviceId) params.set("clientDeviceId", String(clientDeviceId));
        if (resolvedLocationIds.length > 0) {
          params.set("selectedLocationIds", resolvedLocationIds.join(","));
        } else if (locationName) {
          params.set("location", String(locationName));
        }
        params.set("shiftDate", activeDate);
        params.set("from", activeDate);

        const response = await manageFetch(
          `${BASE_URL}/api/jobs/manage/dashboard?${params.toString()}`,
          { signal: controller.signal },
        );
        if (response.status === 401 || response.status === 403) {
          navigate(`/manage/signin?${params.toString()}`, { replace: true });
          return;
        }
        if (!response.ok) {
          throw new Error(`Failed to load shifts (${response.status})`);
        }
        const payload = await response.json();

        if (requestSeq !== requestSeqRef.current) return;
        const nextRows = Array.isArray(payload?.data?.rows) ? payload.data.rows : [];
        const selectedShift = payload?.data?.primaryShift || null;
        const nextGroupSummary = payload?.data?.groupSummary || null;
        const nextCapabilities = payload?.data?.capabilities || contextCapabilities || null;

        setLiveShift(selectedShift);
        setRows(nextRows);
        setGroupSummary(nextGroupSummary);
        setCapabilities(nextCapabilities);
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (requestSeq !== requestSeqRef.current) return;
        setLiveShift(null);
        setRows([]);
        setGroupSummary(null);
        setLoadError(err?.message || "Failed to load dashboard data.");
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    };

    loadLiveShiftData();
    return () => {
      controller.abort();
    };
  }, [
    shiftId,
    manageContext.timesheetId,
    clientId,
    clientDeviceId,
    locationName,
    activeDate,
    contextCapabilities,
    navigate,
    selectedLocationNames,
    resolvedLocationIds,
  ]);

  const displaySummaryCounts = useMemo(
    () => summaryCountsFromRowBadges(rows),
    [rows],
  );

  const formatTime = (date) => formatAppTime(date);

  const summaryCards = [
    { label: "Late", value: displaySummaryCounts.late, bg: "bg-rose-300" },
    { label: "Checked In", value: displaySummaryCounts.checkedIn, bg: "bg-emerald-300" },
    { label: "Pending Signoff", value: displaySummaryCounts.pendingSignoff, bg: "bg-cyan-400" },
    { label: "Pending Review", value: displaySummaryCounts.pendingSignoffReview, bg: "bg-sky-400" },
    { label: "Signoff Complete", value: displaySummaryCounts.signoffComplete, bg: "bg-indigo-300" },
  ];

  const resolvedLocation =
    selectedLocationNames.length > 0 ? selectedLocationNames.join(", ") : locationName || "Unspecified Location";
  const resolvedStartDate = groupSummary?.startDate || liveShift?.startDate || activeDate;
  const resolvedEndDate = groupSummary?.endDate || liveShift?.endDate || activeDate;
  const resolvedPosition = liveShift?.position || position || "Shift";
  const resolvedStaffName =
    groupSummary?.staffNames?.length > 1
      ? `${groupSummary.staffNames.length} staff`
      : liveShift?.staffName || staffName || "Assigned Staff";
  const showTimesheetSignoff = capabilities?.canSignoffTimesheets === true;
  const showManageDevice = capabilities?.canManageDevice !== false;

  // When Manage Device selected concrete location IDs, listing is scoped by those IDs only.
  // Do not persist primary-shift display `locationName` — it becomes `?location=` on the next
  // fetch and re-enables a conflicting anchor with the scoped IN (...) list (404).
  const formatRangeTime = (start, end) => {
    if (!start || !end) return "Time not available";
    return `${formatAppTime(start)} - ${formatAppTime(end)}`;
  };

  const formatCheckInTime = (iso) => formatAppTime(iso);

  const computeHours = (start, end) => {
    const startD = new Date(start);
    const endD = new Date(end);
    if (Number.isNaN(startD.getTime()) || Number.isNaN(endD.getTime())) return "0h";
    const diff = Math.max(0, (endD.getTime() - startD.getTime()) / 3600000);
    const rounded = Math.round(diff * 10) / 10;
    return `${rounded}h`;
  };
  const shiftCountLabel = `${groupSummary?.shiftCount || rows.length}/${groupSummary?.shiftCount || rows.length}`;
  const totalHoursLabel = groupSummary?.totalHours != null
    ? `${groupSummary.totalHours}h`
    : computeHours(resolvedStartDate, resolvedEndDate);
  const scheduleHeading = scheduleHeadingFromDate(activeDate);

  const enrichedManageContext = {
    ...manageContext,
    clientId: manageContext.clientId || liveShift?.clientId || groupSummary?.clientId || null,
    locationName: resolvedLocation !== "Unspecified Location" ? resolvedLocation : manageContext.locationName || "",
    startDate: activeDate,
    endDate: activeDate,
    shiftDate: activeDate,
    position: resolvedPosition !== "Shift" ? resolvedPosition : manageContext.position || "",
    staffName:
      resolvedStaffName !== "Assigned Staff" ? resolvedStaffName : manageContext.staffName || "",
    dateLabel: shortDateLabel(activeDate),
    capabilities,
  };

  useEffect(() => {
    if (!liveShift && !groupSummary) return;
    saveManageContext(enrichedManageContext);
  }, [liveShift, groupSummary, enrichedManageContext]);

  const handleManageDevice = () => {
    navigate("/manage/device", {
      state: {
        manageContext: enrichedManageContext,
      },
    });
  };

  const handleManageSignoff = () => {
    navigate("/manage/signoff", {
      state: {
        manageContext: enrichedManageContext,
      },
    });
  };

  const handleSignOut = () => {
    const fromDay =
      toDateOnly(groupSummary?.startDate) ||
      toDateOnly(manageContext.startDate) ||
      toDateOnly(manageContext.shiftDate) ||
      toDateOnly(activeDate) ||
      getTodayDateOnly();
    const toDay =
      toDateOnly(groupSummary?.endDate) ||
      toDateOnly(manageContext.endDate) ||
      fromDay;
    const shiftDay =
      toDateOnly(activeDate) ||
      toDateOnly(manageContext.shiftDate) ||
      fromDay;

    const locForSignin = resolvedLocation !== "Unspecified Location" ? resolvedLocation : "";

    const clientIdForSignin =
      manageContext.clientId || liveShift?.clientId || groupSummary?.clientId || null;

    const params = new URLSearchParams({
      name: manageContext.clientName || "",
      email: manageContext.email || "",
    });
    if (shiftId) params.set("shiftId", String(shiftId));
    if (manageContext.timesheetId) {
      params.set("timesheetId", String(manageContext.timesheetId));
    }
    if (clientIdForSignin) params.set("clientId", String(clientIdForSignin));
    if (locForSignin) params.set("location", String(locForSignin));
    if (shiftDay) params.set("shiftDate", shiftDay);
    if (fromDay) params.set("from", fromDay);
    if (toDay) params.set("to", toDay);
    if (manageContext.accountId) {
      params.set("accountId", String(manageContext.accountId));
    }
    if (clientDeviceId) {
      params.set("clientDeviceId", String(clientDeviceId));
    }
    if (Array.isArray(manageContext.selectedLocationIds) && manageContext.selectedLocationIds.length) {
      params.set("selectedLocationIds", manageContext.selectedLocationIds.join(","));
    }

    const signinStateContext = {
      ...manageContext,
      clientId: clientIdForSignin || manageContext.clientId,
      locationName: locForSignin,
      shiftDate: shiftDay,
      startDate: fromDay,
      endDate: toDay,
      capabilities: capabilities ?? manageContext.capabilities ?? null,
    };

    clearManageSession();
    clearManageContext();
    navigate(`/manage/signin?${params.toString()}`, {
      replace: true,
      state: { manageContext: signinStateContext },
    });
  };
  const handlePrevDate = () => {
    setActiveDate((prev) => shiftDateOnly(prev, -1));
  };

  const handleNextDate = () => {
    setActiveDate((prev) => shiftDateOnly(prev, 1));
  };

  const handleLogoClick = () => {
    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.has("clientId") && !urlParams.has("name") && !urlParams.has("client")) {
      navigate("/device");
      return;
    }
    const checkinParams = new URLSearchParams();
    if (clientId) checkinParams.set("clientId", String(clientId));
    if (manageContext.shiftDate) checkinParams.set("shiftDate", manageContext.shiftDate);
    if (manageContext.startDate) checkinParams.set("from", manageContext.startDate);
    if (manageContext.endDate) checkinParams.set("to", manageContext.endDate);
    if (clientName) checkinParams.set("client", clientName);
    if (manageContext.locationName) checkinParams.set("location", manageContext.locationName);
    
    const forceLoginVal = urlParams.get("forceLogin") || "1";
    checkinParams.set("forceLogin", forceLoginVal);

    navigate(`/checkin?${checkinParams.toString()}`);
  };

  return (
    <div className="flex h-screen max-h-[100dvh] flex-col overflow-hidden bg-gray-50">
      <div className="flex shrink-0 justify-between items-start p-4">
        <div className="cursor-pointer" onClick={handleLogoClick}>
          <img src={talentLogo2} alt="Talent Logo" className="h-12 w-auto" />
        </div>

        <div className="text-right">
          <div className="text-xl font-bold text-gray-800">{formatTime(currentTime)}</div>
          <div className="text-sm text-gray-600">{clientName}</div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-4">
        <div className="mx-auto w-full max-w-xl pb-4">
          <div className="mb-4 flex items-center justify-center gap-3 text-gray-700">
            <button
              type="button"
              onClick={handlePrevDate}
              className="text-2xl leading-none"
              aria-label="Previous day schedule"
            >
              &#8249;
            </button>
            <h1 className="text-center text-2xl">{scheduleHeading}</h1>
            <button
              type="button"
              onClick={handleNextDate}
              className="text-2xl leading-none"
              aria-label="Next day schedule"
            >
              &#8250;
            </button>
          </div>

          {loading && <div className="text-center text-xs text-gray-500 mb-2">Loading live shift data...</div>}
          {loadError && <div className="text-center text-xs text-red-600 mb-2">{loadError}</div>}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            {summaryCards.map((card) => (
              <div key={card.label} className={`${card.bg} rounded px-2 py-2 text-center text-gray-700`}>
                <div className="text-base font-semibold leading-none">{card.value}</div>
                <div className="text-[10px] mt-1">{card.label}</div>
              </div>
            ))}
          </div>

          {selectedLocationNames.length === 0 ? (
            <div className="border border-gray-200 rounded bg-white overflow-hidden">
              <div className="px-3 py-3 text-sm text-gray-500">No selected locations found for this schedule.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {selectedLocationNames.map((locationTitle) => {
                const locationRows = rows.filter(
                  (row) =>
                    String(row?.locationName || row?.location || "").trim() === String(locationTitle).trim(),
                );
                return (
                  <div key={locationTitle} className="border border-gray-200 rounded bg-white overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-100 border-b border-gray-200">
                      <div className="font-medium text-gray-700">{locationTitle}</div>
                      <div className="text-right text-xs text-gray-600">
                        <div className="font-semibold text-sm leading-none">
                          {locationRows.length} shifts
                        </div>
                        <div>HOURS SHIFTS</div>
                      </div>
                    </div>
                    {locationRows.length ? (
                      locationRows.map((row) => (
                        <div key={`${locationTitle}-${row.id}-${row.shiftAccountId || "row"}`} className="border-t border-gray-100 last:border-b-0">
                          <div className="px-3 py-2 text-xs text-gray-500">
                            {formatRangeTime(row.startDate, row.endDate)}
                            {row.checkinDate && formatCheckInTime(row.checkinDate) ? (
                              <span className="block mt-0.5 text-gray-600">
                                Check in: {formatCheckInTime(row.checkinDate)}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center justify-between px-3 py-2 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-700">{row.position || "Shift"}</span>
                              <StaffAvatarCircle
                                accountId={row.assignedAccountId}
                                employee={
                                  Number(row.assignedAccountId) > 0
                                    ? {
                                        account_id: row.assignedAccountId,
                                        accountId: row.assignedAccountId,
                                        name: row.assignedStaffName || row.staffName || "Staff",
                                        image_time: row.imageTime ?? row.image_time ?? null,
                                        image_type: row.imageType ?? row.image_type ?? null,
                                      }
                                    : null
                                }
                                name={row.assignedStaffName || row.staffName}
                                className="h-5 w-5 shrink-0"
                                textClassName="text-[10px] font-medium text-gray-700"
                              />
                              <span className="text-gray-700">{row.assignedStaffName || row.staffName || "Assigned Staff"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{Number(row.workedHours || 0).toFixed(2)}h</span>
                              {(() => {
                                const ci = checkInTimeStatusFromManageRow(row);
                                return <span className={`status-badge ${ci.badgeClass}`}>{ci.label}</span>;
                              })()}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-sm text-gray-500">No assigned rows found for this location.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showTimesheetSignoff && (
        <div className="shrink-0 px-4 pb-1">
          <button
            type="button"
            onClick={handleManageSignoff}
            className="w-full text-red-500 py-1 text-sm font-semibold hover:text-red-600"
          >
            SIGN OFF ON TIMESHEETS
          </button>
        </div>
      )}

      {showManageDevice && (
        <div className="shrink-0 px-4 pb-2">
          <button
            type="button"
            onClick={handleManageDevice}
            className="w-full bg-gray-100 text-gray-700 py-2 text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            MANAGE DEVICE
          </button>
        </div>
      )}

      <div className="shrink-0 px-4 pb-4">
        <button
          type="button"
          onClick={handleSignOut}
          className="w-full text-gray-700 py-1 text-xs font-medium hover:text-black"
        >
          SIGNOUT
        </button>
      </div>
    </div>
  );
}
